import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ROUTE_STATUS_LABELS,
  WHOLESALE_STATUS_LABELS,
  type ProductDTO,
  type RouteOptimizeDTO,
  type RouteStatus,
  type UnitType,
  type WholesaleRouteDTO,
  type WholesaleRouteSuggestionsDTO,
} from '@bifeai/shared';
import { Badge, Button, Callout, Dialog, EmptyState, Field, Input, PageHeader, Select, Skeleton, Textarea, useToast, type Tone } from '@bifeai/ui';
import { ArrowDown, ArrowUp, MapPin, Navigation, Plus, Printer, Route as RouteIcon, Search, Smartphone, Sparkles, Trash2, Truck } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { RouteMap, googleRouteUrl } from '../../components/map/RouteMap';
import { api, errorMessage } from '../../lib/api';
import { useProducts } from '../../lib/catalog';
import { formatDate, formatMoney } from '../../lib/format';
import { useSession } from '../../lib/session';
import '../orders-chat/orders-chat.css';
import './wholesale.css';
import { PrintNotesButton, STATUS_TONES, parseQty, qtyInput, qtyText, useStaff, useVehicles, useWsSettings } from './common';

const ROUTE_TONES: Record<RouteStatus, Tone> = { PLANNED: 'warning', ON_ROUTE: 'info', CLOSED: 'neutral' };
export const todayLocal = (): string => new Date().toLocaleDateString('sv-SE');

/** Hojas de ruta: qué camioneta sale, con quién, a qué clientes, con qué carga, y cómo vuelve. */
export function RoutesPage() {
  const { can } = useSession();
  const manage = can('wholesale.manage');
  const [mode, setMode] = useState<'open' | 'day'>('open');
  const [date, setDate] = useState(todayLocal());
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ['wholesale', 'routes', mode, date],
    queryFn: () => api.get<WholesaleRouteDTO[]>(mode === 'open' ? '/wholesale/routes?status=open' : `/wholesale/routes?date=${date}`),
    refetchInterval: 30_000,
  });
  const routes = q.data ?? [];
  const open = routes.find((r) => r.id === openId);
  return (
    <>
      <PageHeader
        title="Hojas de ruta"
        subtitle="Armá cada salida con los pedidos listos, los clientes del día y la carga para vender en la calle. El repartidor la ve en su celular, aunque no tenga señal."
        actions={
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Link to="/reparto" className="bf-btn" style={{ textDecoration: 'none' }}>
              <Smartphone size={16} aria-hidden /> Pantalla del repartidor
            </Link>
            {manage && (
              <Button variant="primary" icon={<Plus size={16} />} onClick={() => setCreating(true)}>
                Nueva hoja de ruta
              </Button>
            )}
          </div>
        }
      />
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
        <Select value={mode} onChange={(e) => setMode(e.target.value as 'open' | 'day')} style={{ width: 'auto' }} aria-label="Qué mostrar">
          <option value="open">Abiertas (armándose y en la calle)</option>
          <option value="day">Por día</option>
        </Select>
        {mode === 'day' && <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ width: 'auto' }} aria-label="Día" />}
      </div>
      {q.isPending ? (
        <Skeleton style={{ height: 180 }} />
      ) : routes.length === 0 ? (
        <EmptyState
          icon={<RouteIcon />}
          title={mode === 'open' ? 'No hay salidas abiertas' : 'No hubo salidas ese día'}
          text={
            manage
              ? 'Armá una hoja de ruta: te sugerimos los pedidos listos y los clientes que se visitan ese día.'
              : 'Cuando te asignen una salida, aparece acá.'
          }
          action={
            manage ? (
              <Button variant="primary" onClick={() => setCreating(true)}>
                Nueva hoja de ruta
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="bf-ws-cards">
          {routes.map((r) => (
            <RouteCard key={r.id} r={r} onOpen={() => setOpenId(r.id)} />
          ))}
        </div>
      )}
      {creating && <RouteFormDialog onClose={() => setCreating(false)} onSaved={(r) => (setCreating(false), setOpenId(r.id))} />}
      {open && <RouteDetailDialog route={open} onClose={() => setOpenId(null)} />}
    </>
  );
}

function RouteCard({ r, onOpen }: { r: WholesaleRouteDTO; onOpen: () => void }) {
  const done = r.stops.filter((s) => s.status !== 'PENDING').length;
  return (
    <button type="button" className="bf-ws-card bf-pick" onClick={onOpen} style={{ display: 'grid' }}>
      <div className="bf-ws-card-head">
        <div>
          <div className="bf-ws-card-name">Salida #{r.number}</div>
          <div className="bf-cell-sub">{formatDate(r.date)}</div>
        </div>
        <Badge tone={ROUTE_TONES[r.status]}>{ROUTE_STATUS_LABELS[r.status]}</Badge>
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <Truck size={15} aria-hidden /> {r.driverName}
        {r.vehicleName && <span className="bf-cell-sub">· {r.vehicleName}</span>}
      </div>
      <div className="bf-ws-route-progress" aria-label={`${done} de ${r.stops.length} paradas hechas`}>
        <span style={{ width: `${r.stops.length ? (done / r.stops.length) * 100 : 0}%` }} />
      </div>
      <div className="bf-cell-sub">
        {done} de {r.stops.length} paradas · {r.stops.filter((s) => s.orderId).length} pedidos
        {r.load.length > 0 && ` · carga: ${r.load.length} productos`}
        {r.spotSales.length > 0 && ` · ${r.spotSales.length} ventas en la calle`}
      </div>
      {r.collected.cashCents + r.collected.otherCents > 0 && (
        <div>
          Cobrado: <strong>{formatMoney(r.collected.cashCents + r.collected.otherCents)}</strong>
          {r.collected.unsettledCashCents > 0 && (
            <span className="bf-cell-sub"> · {formatMoney(r.collected.unsettledCashCents)} en efectivo sin rendir</span>
          )}
        </div>
      )}
    </button>
  );
}

// ---------------------------------------------------------------- armar o cambiar
type StopRow = { customerId: string; orderId: string | null; name: string; sub: string };
type LoadRow = { productId: string; name: string; unitType: UnitType; qty: string };

function RouteFormDialog({ route, onClose, onSaved }: { route?: WholesaleRouteDTO; onClose: () => void; onSaved: (r: WholesaleRouteDTO) => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const staff = useStaff();
  const settings = useWsSettings();
  const auto = settings.data?.routeOrder !== 'MANUAL';
  // Una vez que se mueve una parada a mano, el orden automático no la pisa.
  const [manual, setManual] = useState(false);
  const vehicles = useVehicles();
  const [date, setDate] = useState(route?.date ?? todayLocal());
  const [driver, setDriver] = useState(route?.driverUserId ?? '');
  const [vehicle, setVehicle] = useState(route?.vehicleId ?? '');
  const [notes, setNotes] = useState(route?.notes ?? '');
  const [departTime, setDepartTime] = useState('');
  const [stops, setStops] = useState<StopRow[]>(
    route?.stops.map((s) => ({
      customerId: s.customerId,
      orderId: s.orderId,
      name: s.customerName,
      sub: s.orderNumber ? `Pedido #${s.orderNumber}` : 'Visita',
    })) ?? [],
  );
  const [load, setLoad] = useState<LoadRow[]>(
    route?.load.map((l) => ({ productId: l.productId, name: l.productName, unitType: l.unitType, qty: qtyInput(l.unitType, l.qtyLoaded) })) ?? [],
  );
  const [find, setFind] = useState('');
  const sugg = useQuery({
    queryKey: ['wholesale', 'routes', 'suggestions', date],
    queryFn: () => api.get<WholesaleRouteSuggestionsDTO>(`/wholesale/routes/suggestions?date=${date}`),
    enabled: /^\d{4}-\d{2}-\d{2}$/.test(date),
  });
  const products = useProducts({ search: find.trim().length >= 2 ? find.trim() : undefined });
  const matches = useMemo(
    () =>
      find.trim().length >= 2
        ? (products.data ?? []).filter((p: ProductDTO) => p.isActive && p.tracksStock && !load.some((l) => l.productId === p.id)).slice(0, 6)
        : [],
    [find, products.data, load],
  );
  const pendingOrders = (sugg.data?.orders ?? []).filter((o) => !stops.some((s) => s.orderId === o.id));
  const pendingVisits = (sugg.data?.customers ?? []).filter((c) => !stops.some((s) => s.customerId === c.id));
  const addOrder = (o: WholesaleRouteSuggestionsDTO['orders'][number]) =>
    setStops((s) => [
      ...s,
      { customerId: o.customerId, orderId: o.id, name: o.customerName, sub: `Pedido #${o.number} · ${formatMoney(o.totalCents)}` },
    ]);
  const move = (i: number, d: -1 | 1) =>
    setStops((s) => {
      const n = [...s];
      const j = i + d;
      if (j < 0 || j >= n.length) return s;
      [n[i], n[j]] = [n[j]!, n[i]!];
      return n;
    });
  const badLoad = load.some((l) => !parseQty(l.unitType, l.qty));
  const [opt, setOpt] = useState<RouteOptimizeDTO | null>(null);
  const optimize = useMutation({
    mutationFn: () =>
      api.post<RouteOptimizeDTO>('/wholesale/routes/optimize', {
        branchId: route?.branchId ?? null,
        departTime: departTime || null,
        stops: stops.map((s) => ({ customerId: s.customerId, orderId: s.orderId })),
      }),
    onSuccess: (r) => {
      setOpt(r);
      setStops((cur) => r.stops.map((x) => cur.find((s) => s.customerId === x.customerId && s.orderId === x.orderId)!).filter(Boolean));
    },
    onError: (e) => toast.error('No se pudo ordenar', errorMessage(e)),
  });
  // Automático: cada vez que se suma o se saca una parada, se vuelve a ordenar por cercanía.
  const stopsKey = stops
    .map((s) => `${s.customerId}:${s.orderId ?? ''}`)
    .sort()
    .join(',');
  const firstKey = useState(stopsKey)[0];
  useEffect(() => {
    if (!auto || manual || stops.length < 2 || (route && stopsKey === firstKey && !departTime)) return;
    optimize.mutate();
  }, [stopsKey, auto, manual, departTime]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: async () => {
      const loadBody = load.map((l) => ({ productId: l.productId, qty: parseQty(l.unitType, l.qty) ?? 0 }));
      const stopsBody = stops.map((s) => ({ customerId: s.customerId, orderId: s.orderId }));
      if (route) {
        return api.put<WholesaleRouteDTO>(`/wholesale/routes/${route.id}`, {
          vehicleId: vehicle || null,
          driverUserId: driver,
          notes: notes || null,
          stops: stopsBody,
          load: loadBody,
        });
      }
      const r = await api.post<WholesaleRouteDTO>('/wholesale/routes', {
        date,
        vehicleId: vehicle || null,
        driverUserId: driver,
        notes: notes || null,
        orderIds: stops.filter((s) => s.orderId).map((s) => s.orderId),
        customerIds: stops.filter((s) => !s.orderId).map((s) => s.customerId),
        load: loadBody,
      });
      // Respeta el orden de paradas elegido.
      return api.put<WholesaleRouteDTO>(`/wholesale/routes/${r.id}`, {
        vehicleId: vehicle || null,
        driverUserId: driver,
        notes: notes || null,
        stops: stopsBody,
        load: loadBody,
      });
    },
    onSuccess: async (r) => {
      await qc.invalidateQueries({ queryKey: ['wholesale'] });
      toast.success(route ? 'Hoja de ruta actualizada' : `Salida #${r.number} armada`);
      onSaved(r);
    },
    onError: (e) => toast.error('No se pudo guardar', errorMessage(e)),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={route ? `Cambiar salida #${route.number}` : 'Nueva hoja de ruta'} wide>
      <div style={{ display: 'grid', gap: 16 }}>
        <div className="bf-ws-form">
          <Field label="Día">
            <Input type="date" value={date} disabled={!!route} onChange={(e) => setDate(e.target.value)} />
          </Field>
          <Field label="Repartidor">
            <Select value={driver} onChange={(e) => setDriver(e.target.value)}>
              <option value="">Elegí</option>
              {(staff.data ?? [])
                .filter((s) => s.canDeliver || s.userId === driver)
                .map((s) => (
                  <option key={s.userId} value={s.userId}>
                    {s.name}
                  </option>
                ))}
            </Select>
          </Field>
          <Field label="Sale a las" hint={`Para estimar la llegada a cada parada. Vacío: ${settings.data?.routeStartTime ?? '08:00'}.`}>
            <Input type="time" value={departTime} onChange={(e) => setDepartTime(e.target.value)} />
          </Field>
          <Field label="Camioneta" hint={vehicles.data?.length === 0 ? 'Cargalas en Configurar mayorista.' : undefined}>
            <Select value={vehicle} onChange={(e) => setVehicle(e.target.value)}>
              <option value="">Sin asignar</option>
              {(vehicles.data ?? []).map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                  {v.plate ? ` · ${v.plate}` : ''}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <div className="bf-ws-grid">
          <div style={{ display: 'grid', gap: 10, alignContent: 'start' }}>
            <strong>Para agregar</strong>
            {sugg.isPending && <Skeleton style={{ height: 80 }} />}
            {pendingOrders.length > 0 && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span className="bf-cell-sub">Pedidos para repartir</span>
                  <Button size="sm" variant="ghost" onClick={() => pendingOrders.forEach(addOrder)}>
                    Agregar todos
                  </Button>
                </div>
                {pendingOrders.map((o) => (
                  <button key={o.id} type="button" className="bf-pick" onClick={() => addOrder(o)}>
                    <Plus size={14} aria-hidden />
                    <div style={{ display: 'grid' }}>
                      <strong>{o.customerName}</strong>
                      <span className="bf-cell-sub">
                        #{o.number} · {formatMoney(o.totalCents)}
                        {o.zoneName ? ` · ${o.zoneName}` : ''}
                      </span>
                    </div>
                  </button>
                ))}
              </>
            )}
            {pendingVisits.length > 0 && (
              <>
                <span className="bf-cell-sub">Se visitan ese día (sin pedido)</span>
                {pendingVisits.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    className="bf-pick"
                    onClick={() => setStops((s) => [...s, { customerId: c.id, orderId: null, name: c.name, sub: 'Visita' }])}
                  >
                    <MapPin size={14} aria-hidden />
                    <div style={{ display: 'grid' }}>
                      <strong>{c.name}</strong>
                      <span className="bf-cell-sub">{[c.zoneName, c.address].filter(Boolean).join(' · ') || 'Visita'}</span>
                    </div>
                  </button>
                ))}
              </>
            )}
            {!sugg.isPending && !pendingOrders.length && !pendingVisits.length && (
              <span className="bf-cell-sub">No hay más pedidos listos ni clientes para visitar ese día.</span>
            )}
          </div>
          <div style={{ display: 'grid', gap: 8, alignContent: 'start' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <strong>Paradas, en orden ({stops.length})</strong>
              {stops.length > 1 && (
                <Button size="sm" icon={<Sparkles size={14} />} loading={optimize.isPending} onClick={() => (setManual(false), optimize.mutate())}>
                  Ordenar por cercanía
                </Button>
              )}
            </div>
            {stops.length > 1 && (
              <span className="bf-cell-sub">
                {auto && !manual
                  ? 'Orden automático por cercanía. Si movés una parada con las flechas, queda como la dejaste.'
                  : 'Orden a mano. Tocá "Ordenar por cercanía" para que lo arme el sistema.'}
              </span>
            )}
            {opt && (
              <Callout tone={opt.missing.length ? 'warning' : 'success'}>
                {opt.distanceKm > 0 && (
                  <div>
                    Recorrido de unos <strong>{opt.distanceKm.toLocaleString('es-AR')} km</strong>
                    {opt.previousKm > opt.distanceKm ? ` (antes ${opt.previousKm.toLocaleString('es-AR')} km)` : ''} y{' '}
                    <strong>{opt.driveMinutes} min</strong> de manejo saliendo a las {opt.departTime},{' '}
                    {opt.street ? 'calculado por calle.' : 'calculado en línea recta (aproximado).'}
                  </div>
                )}
                {opt.etas.some((e) => e.late) && (
                  <div>
                    No llega a horario a:{' '}
                    {opt.etas
                      .filter((e) => e.late)
                      .map((e) => stops.find((s) => s.customerId === e.customerId)?.name)
                      .join(', ')}
                    . Probá salir más temprano o moverlo con las flechas.
                  </div>
                )}
                {!opt.origin && <div>El local de salida no tiene ubicación: marcala en Configurar mayorista para que arranque desde ahí.</div>}
                {opt.missing.length > 0 && (
                  <div>
                    Sin ubicación en el mapa (quedan al final): {opt.missing.map((m) => m.name).join(', ')}. Ubicalos en la ficha del cliente.
                  </div>
                )}
              </Callout>
            )}
            {stops.length === 0 && <span className="bf-cell-sub">Agregá pedidos o visitas de la columna de la izquierda.</span>}
            {stops.map((s, i) => (
              <div key={`${s.customerId}-${s.orderId ?? 'v'}`} className="bf-ws-stop-row">
                <span className="bf-ws-stop-n">{i + 1}</span>
                <div style={{ display: 'grid', flex: 1, minWidth: 0 }}>
                  <strong>{s.name}</strong>
                  <span className="bf-cell-sub">
                    {s.sub}
                    {(() => {
                      const e = !manual ? opt?.etas.find((x) => x.customerId === s.customerId) : undefined;
                      return e ? ` · llega ~${e.eta}${e.window ? ` (recibe ${e.window})` : ''}${e.late ? ' · fuera de horario' : ''}` : '';
                    })()}
                  </span>
                </div>
                <button type="button" className="bf-order-del" aria-label="Subir" disabled={i === 0} onClick={() => (setManual(true), move(i, -1))}>
                  <ArrowUp size={14} aria-hidden />
                </button>
                <button
                  type="button"
                  className="bf-order-del"
                  aria-label="Bajar"
                  disabled={i === stops.length - 1}
                  onClick={() => (setManual(true), move(i, 1))}
                >
                  <ArrowDown size={14} aria-hidden />
                </button>
                <button
                  type="button"
                  className="bf-order-del"
                  aria-label={`Quitar ${s.name}`}
                  onClick={() => setStops(stops.filter((_, j) => j !== i))}
                >
                  <Trash2 size={14} aria-hidden />
                </button>
              </div>
            ))}
          </div>
        </div>

        <Field
          label="Carga para vender en la calle"
          hint="Sale del stock del local al arrancar la salida; lo que vuelve, entra de nuevo. Los pedidos ya preparados no van acá."
        >
          <div style={{ position: 'relative' }}>
            <Input placeholder="Buscar producto" value={find} onChange={(e) => setFind(e.target.value)} aria-label="Buscar producto para la carga" />
            <Search size={16} aria-hidden style={{ position: 'absolute', right: 12, top: 12, opacity: 0.5 }} />
          </div>
        </Field>
        {matches.length > 0 && (
          <div style={{ display: 'grid', gap: 4 }}>
            {matches.map((p: ProductDTO) => (
              <button
                key={p.id}
                type="button"
                className="bf-pick"
                onClick={() => {
                  setLoad([...load, { productId: p.id, name: p.name, unitType: p.unitType, qty: '' }]);
                  setFind('');
                }}
              >
                <strong>{p.name}</strong>
                <span className="bf-cell-sub">{p.unitType === 'WEIGHT' ? 'por kilo' : 'por unidad'}</span>
              </button>
            ))}
          </div>
        )}
        {load.length > 0 && (
          <div style={{ display: 'grid', gap: 6 }}>
            {load.map((l, i) => (
              <div key={l.productId} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <strong style={{ flex: 1 }}>{l.name}</strong>
                <Input
                  value={l.qty}
                  inputMode="decimal"
                  placeholder={l.unitType === 'WEIGHT' ? 'Kilos' : 'Unidades'}
                  aria-label={`Cantidad de ${l.name}`}
                  style={{ width: 120 }}
                  onChange={(e) => setLoad(load.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)))}
                />
                <span className="bf-cell-sub" style={{ width: 24 }}>
                  {l.unitType === 'WEIGHT' ? 'kg' : 'u.'}
                </span>
                <button
                  type="button"
                  className="bf-order-del"
                  aria-label={`Quitar ${l.name}`}
                  onClick={() => setLoad(load.filter((_, j) => j !== i))}
                >
                  <Trash2 size={14} aria-hidden />
                </button>
              </div>
            ))}
          </div>
        )}
        <Field label="Notas para el repartidor">
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" loading={save.isPending} disabled={!driver || stops.length === 0 || badLoad} onClick={() => save.mutate()}>
            {route ? 'Guardar' : 'Armar salida'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------- detalle, salida y vuelta
function RouteDetailDialog({ route: r, onClose }: { route: WholesaleRouteDTO; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { can } = useSession();
  const manage = can('wholesale.manage');
  const [editing, setEditing] = useState(false);
  const [closing, setClosing] = useState(false);
  const [returned, setReturned] = useState<Record<string, string>>({});
  const [closeNotes, setCloseNotes] = useState('');
  useEffect(() => {
    setReturned(Object.fromEntries(r.load.map((l) => [l.productId, qtyInput(l.unitType, l.qtyAvailable)])));
  }, [r.id, closing]); // eslint-disable-line react-hooks/exhaustive-deps
  const done = async () => qc.invalidateQueries({ queryKey: ['wholesale'] });
  const start = useMutation({
    mutationFn: () => api.post<WholesaleRouteDTO>(`/wholesale/routes/${r.id}/start`),
    onSuccess: async () => {
      await done();
      toast.success(`Salió la salida #${r.number}`, 'La carga se descontó del stock.');
    },
    onError: (e) => toast.error('No se pudo arrancar', errorMessage(e)),
  });
  const del = useMutation({
    mutationFn: () => api.del(`/wholesale/routes/${r.id}`),
    onSuccess: async () => {
      await done();
      onClose();
    },
    onError: (e) => toast.error('No se pudo borrar', errorMessage(e)),
  });
  const close = useMutation({
    mutationFn: () =>
      api.post<WholesaleRouteDTO>(`/wholesale/routes/${r.id}/close`, {
        returned: r.load.map((l) => ({ productId: l.productId, qty: parseQty(l.unitType, returned[l.productId] ?? '0') ?? 0 })),
        notes: closeNotes || null,
      }),
    onSuccess: async () => {
      await done();
      setClosing(false);
      toast.success(`Salida #${r.number} cerrada`, 'Lo que volvió entró al stock.');
    },
    onError: (e) => toast.error('No se pudo cerrar', errorMessage(e)),
  });
  const badReturn = r.load.some((l) => {
    const q = parseQty(l.unitType, returned[l.productId] ?? '');
    return q === null || q > l.qtyAvailable;
  });

  if (editing) return <RouteFormDialog route={r} onClose={() => setEditing(false)} onSaved={() => setEditing(false)} />;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={`Salida #${r.number} · ${formatDate(r.date)}`} wide>
      <div style={{ display: 'grid', gap: 16 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <Badge tone={ROUTE_TONES[r.status]}>{ROUTE_STATUS_LABELS[r.status]}</Badge>
          <span>
            <Truck size={15} aria-hidden style={{ verticalAlign: -2 }} /> {r.driverName}
            {r.vehicleName ? ` · ${r.vehicleName}` : ''} · sale de {r.branchName}
          </span>
        </div>
        {r.notes && <Callout tone="info">{r.notes}</Callout>}
        <RouteMapBlock r={r} />

        <div className="bf-table-wrap">
          <table className="bf-table">
            <thead>
              <tr>
                <th style={{ width: 36 }}>#</th>
                <th>Cliente</th>
                <th>Pedido</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {r.stops.map((s) => (
                <tr key={s.id}>
                  <td>{s.position}</td>
                  <td>
                    <div>{s.customerName}</div>
                    <div className="bf-cell-sub">{s.address ?? 'Sin dirección'}</div>
                    {s.note && <div className="bf-cell-sub">“{s.note}”</div>}
                  </td>
                  <td>
                    {s.orderNumber ? (
                      <>
                        <div>
                          #{s.orderNumber} · {formatMoney(s.orderTotalCents ?? 0)}
                        </div>
                        {s.orderStatus && <Badge tone={STATUS_TONES[s.orderStatus]}>{WHOLESALE_STATUS_LABELS[s.orderStatus]}</Badge>}
                      </>
                    ) : (
                      <span className="bf-cell-sub">Visita</span>
                    )}
                  </td>
                  <td>
                    <Badge tone={s.status === 'DONE' ? 'success' : s.status === 'SKIPPED' ? 'warning' : 'neutral'}>
                      {s.status === 'DONE' ? 'Hecha' : s.status === 'SKIPPED' ? 'No se pudo' : 'Pendiente'}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {r.load.length > 0 && (
          <div className="bf-table-wrap">
            <table className="bf-table">
              <thead>
                <tr>
                  <th>Carga libre</th>
                  <th style={{ textAlign: 'right' }}>Salió</th>
                  <th style={{ textAlign: 'right' }}>Vendido</th>
                  <th style={{ textAlign: 'right' }}>{r.status === 'CLOSED' ? 'Volvió' : 'Queda'}</th>
                  {r.status === 'CLOSED' && <th style={{ textAlign: 'right' }}>Falta</th>}
                </tr>
              </thead>
              <tbody>
                {r.load.map((l) => (
                  <tr key={l.productId}>
                    <td>{l.productName}</td>
                    <td style={{ textAlign: 'right' }}>{qtyText(l.unitType, l.qtyLoaded)}</td>
                    <td style={{ textAlign: 'right' }}>{qtyText(l.unitType, l.qtySold)}</td>
                    <td style={{ textAlign: 'right' }}>{qtyText(l.unitType, l.qtyReturned ?? l.qtyAvailable)}</td>
                    {r.status === 'CLOSED' && (
                      <td style={{ textAlign: 'right' }}>
                        {l.qtyMissing ? <Badge tone={l.qtyMissing > 0 ? 'danger' : 'neutral'}>{qtyText(l.unitType, l.qtyMissing)}</Badge> : '—'}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {(r.spotSales.length > 0 || r.collected.cashCents + r.collected.otherCents > 0) && (
          <div className="bf-ws-form">
            <div>
              <div className="bf-cell-sub">Ventas en la calle</div>
              <strong>{formatMoney(r.spotSales.reduce((t, s) => t + s.totalCents, 0))}</strong>
              <div className="bf-cell-sub">{r.spotSales.map((s) => `#${s.number} ${s.customerName}`).join(' · ') || '—'}</div>
            </div>
            <div>
              <div className="bf-cell-sub">Cobró en efectivo</div>
              <strong>{formatMoney(r.collected.cashCents)}</strong>
              {r.collected.unsettledCashCents > 0 && <div className="bf-cell-sub">{formatMoney(r.collected.unsettledCashCents)} sin rendir</div>}
            </div>
            <div>
              <div className="bf-cell-sub">Otros medios</div>
              <strong>{formatMoney(r.collected.otherCents)}</strong>
            </div>
          </div>
        )}
        {r.closeNotes && <Callout tone="info">Al volver: {r.closeNotes}</Callout>}

        {closing && (
          <div style={{ display: 'grid', gap: 10 }}>
            <strong>¿Qué volvió en la camioneta?</strong>
            {r.load.map((l) => (
              <div key={l.productId} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ flex: 1 }}>
                  {l.productName} <span className="bf-cell-sub">(quedaban {qtyText(l.unitType, l.qtyAvailable)})</span>
                </span>
                <Input
                  value={returned[l.productId] ?? ''}
                  inputMode="decimal"
                  style={{ width: 110 }}
                  aria-label={`Volvió de ${l.productName}`}
                  onChange={(e) => setReturned({ ...returned, [l.productId]: e.target.value })}
                />
                <span className="bf-cell-sub" style={{ width: 24 }}>
                  {l.unitType === 'WEIGHT' ? 'kg' : 'u.'}
                </span>
              </div>
            ))}
            {r.stops.some((s) => s.orderStatus === 'ON_ROUTE') && (
              <Callout tone="warning">Los pedidos que no se entregaron vuelven a “Listo” para salir en otra hoja de ruta.</Callout>
            )}
            <Field label="Notas">
              <Input value={closeNotes} onChange={(e) => setCloseNotes(e.target.value)} placeholder="Ej.: faltó medio kilo de vacío" />
            </Field>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button icon={<Printer size={16} />} onClick={() => printRoute(r)}>
              Imprimir
            </Button>
            <PrintNotesButton
              label="Comprobantes de entrega"
              orderIds={r.stops
                .filter((x) => x.orderId && x.orderStatus && !['PENDING', 'PREPARING', 'CANCELED'].includes(x.orderStatus))
                .map((x) => x.orderId!)}
            />
            {manage && r.status === 'PLANNED' && (
              <Button variant="ghost" loading={del.isPending} onClick={() => del.mutate()}>
                Borrar
              </Button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {manage && r.status === 'PLANNED' && <Button onClick={() => setEditing(true)}>Cambiar</Button>}
            {r.status === 'PLANNED' && (
              <Button variant="primary" loading={start.isPending} onClick={() => start.mutate()}>
                Arrancar salida
              </Button>
            )}
            {manage && r.status === 'ON_ROUTE' && !closing && (
              <Button variant="primary" onClick={() => setClosing(true)}>
                Volvió la camioneta
              </Button>
            )}
            {closing && (
              <>
                <Button onClick={() => setClosing(false)}>Cancelar</Button>
                <Button variant="primary" loading={close.isPending} disabled={badReturn} onClick={() => close.mutate()}>
                  Cerrar salida
                </Button>
              </>
            )}
            {r.status === 'CLOSED' && r.collected.unsettledCashCents > 0 && manage && (
              <Link to="/mayorista/cobros" className="bf-btn bf-btn--primary" style={{ textDecoration: 'none' }}>
                Rendir la plata
              </Link>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
}

const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);

/** Hoja de ruta en papel, para quien prefiere llevarla impresa. */
function printRoute(r: WholesaleRouteDTO) {
  const w = window.open('', '_blank', 'width=800,height=900');
  if (!w) return;
  const rows = r.stops
    .map(
      (s) =>
        `<tr><td>${s.position}</td><td><b>${esc(s.customerName)}</b><br>${esc(s.address ?? '')}${s.phone ? ` · ${esc(s.phone)}` : ''}${s.deliveryNotes ? `<br><i>${esc(s.deliveryNotes)}</i>` : ''}</td><td>${s.orderNumber ? `#${s.orderNumber}<br>${formatMoney(s.orderTotalCents ?? 0)}` : 'Visita'}</td><td>${s.orderDueCents ? formatMoney(s.orderDueCents) : ''}</td><td style="width:120px"></td></tr>`,
    )
    .join('');
  const load = r.load
    .map((l) => `<tr><td>${esc(l.productName)}</td><td>${qtyText(l.unitType, l.qtyLoaded)}</td><td style="width:120px"></td></tr>`)
    .join('');
  w.document.write(`<!doctype html><html lang="es"><head><meta charset="utf-8"><title>Salida #${r.number}</title>
<style>body{font:13px system-ui,sans-serif;margin:24px;color:#111}h1{font-size:18px;margin:0 0 4px}table{width:100%;border-collapse:collapse;margin-top:12px}th,td{border:1px solid #bbb;padding:6px;text-align:left;vertical-align:top}th{background:#f1f1f1}</style></head><body>
<h1>Hoja de ruta · salida #${r.number} · ${formatDate(r.date)}</h1>
<div>Repartidor: <b>${esc(r.driverName)}</b>${r.vehicleName ? ` · Camioneta: ${esc(r.vehicleName)}` : ''} · Sale de ${esc(r.branchName)}</div>
${r.notes ? `<p><i>${esc(r.notes)}</i></p>` : ''}
<table><thead><tr><th>#</th><th>Cliente</th><th>Pedido</th><th>A cobrar</th><th>Firma / cobrado</th></tr></thead><tbody>${rows}</tbody></table>
${load ? `<table><thead><tr><th>Carga libre</th><th>Salió</th><th>Volvió</th></tr></thead><tbody>${load}</tbody></table>` : ''}
<script>window.onload=()=>window.print()</script></body></html>`);
  w.document.close();
}

/** Mapa de la salida con el recorrido y el link para navegarlo en Google Maps. */
export function RouteMapBlock({ r, onlyPending = false }: { r: WholesaleRouteDTO; onlyPending?: boolean }) {
  const located = r.stops.filter((s) => s.lat !== null && s.lng !== null);
  const points = useMemo(
    () => located.map((s) => ({ lat: s.lat!, lng: s.lng!, label: String(s.position), title: s.customerName, done: s.status !== 'PENDING' })),
    [r], // eslint-disable-line react-hooks/exhaustive-deps
  );
  if (!located.length) return null;
  const go = googleRouteUrl(
    r.origin,
    located.filter((s) => !onlyPending || s.status === 'PENDING').map((s) => ({ lat: s.lat!, lng: s.lng! })),
  );
  const missing = r.stops.length - located.length;
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <RouteMap origin={r.origin} points={points} height={300} />
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        {go && (
          <a className="bf-btn bf-btn--sm" href={go} target="_blank" rel="noreferrer">
            <Navigation size={14} aria-hidden /> Navegar el recorrido en Google Maps
          </a>
        )}
        {missing > 0 && <span className="bf-cell-sub">{missing === 1 ? 'Una parada no tiene' : `${missing} paradas no tienen`} ubicación.</span>}
      </div>
    </div>
  );
}
